/**
 * Identifiers.
 *
 * UUIDv7 everywhere, because it sorts by creation time. On a device that has been offline
 * for a day, that means the local event log is already in a sensible order before the
 * server has ever seen it.
 */

import { v7 as uuidv7 } from 'uuid';

export function newId(): string {
  return uuidv7();
}

/** Device codes are the prefix on every ticket number, so they must be short and unmistakable. */
const DEVICE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

export function isDeviceCode(code: string): boolean {
  return /^[A-HJ-NP-Z2-9]{2}$/.test(code);
}

export function deviceCodeFromIndex(index: number): string {
  const n = DEVICE_ALPHABET.length;
  if (index < 0 || index >= n * n) throw new RangeError(`device index out of range: ${index}`);
  return `${DEVICE_ALPHABET[Math.floor(index / n)]}${DEVICE_ALPHABET[index % n]}`;
}

/**
 * Ticket numbers are device-scoped: "A1-000123".
 *
 * This is what makes offline selling safe. Two devices with no connection between them
 * cannot collide, so neither has to ask permission before issuing a number — and nobody
 * ever has to explain a duplicate receipt to a customer.
 */
export function formatTicket(deviceCode: string, seq: number): string {
  return `${deviceCode}-${String(seq).padStart(6, '0')}`;
}

export function parseTicket(ticket: string): { deviceCode: string; seq: number } | null {
  const m = /^([A-HJ-NP-Z2-9]{2})-(\d{6,})$/.exec(ticket.trim().toUpperCase());
  if (!m) return null;
  return { deviceCode: m[1] as string, seq: Number(m[2]) };
}
